import { computed, shallowRef } from "vue";
import { transactionId } from "@/functions/transactionId";
import { defaultLanguage, languageNames, type ISOLanguageKey } from "@/language";

interface DynamicTranslationCatalogResponse {
  version: string;
  locale: string;
  values: Record<string, string>;
  exactIds: number[];
}

interface DynamicTranslationCatalog {
  version: string;
  locale: ISOLanguageKey;
  values: ReadonlyMap<number, string>;
  exactIds: ReadonlySet<number>;
  etag?: string;
}

const supportedLocales = new Set<ISOLanguageKey>(
  Object.keys(languageNames).map((locale) => locale.replaceAll("_", "-") as ISOLanguageKey),
);
const catalogs = new Map<ISOLanguageKey, DynamicTranslationCatalog>();
const inFlightLoads = new Map<ISOLanguageKey, Promise<DynamicTranslationCatalog>>();
type AccessTokenProvider = () => string | undefined | Promise<string | undefined>;
let accessTokenProvider: AccessTokenProvider = () => undefined;

function emptyCatalog(locale: ISOLanguageKey): DynamicTranslationCatalog {
  return {
    version: "0",
    locale,
    values: new Map(),
    exactIds: new Set(),
  };
}

const activeCatalog = shallowRef<DynamicTranslationCatalog>(emptyCatalog(defaultLanguage));

const translationUnavailable: Record<ISOLanguageKey, string> = {
  de: "🌐︎ Keine Übersetzung gepflegt",
  "de-CH": "🌐︎ Keine Übersetzung gepflegt",
  en: "🌐︎ No translation maintained",
  "en-GB": "🌐︎ No translation maintained",
  fr: "🌐︎ Aucune traduction n'est maintenue",
  es: "🌐︎ No se mantiene la traducción",
  pt: "🌐︎ Nenhuma tradução mantida",
  hu: "🌐︎ Nincs fenntartott fordítás",
  pl: "🌐︎ Nie zachowano tłumaczenia",
  it: "🌐︎ Nessuna traduzione trovata",
  "uk-UA": "🌐︎ Перекладу не знайдено",
};

export function normalizeDynamicTranslationLocale(locale: string): ISOLanguageKey | null {
  const normalized = locale.replaceAll("_", "-") as ISOLanguageKey;
  return supportedLocales.has(normalized) ? normalized : null;
}

function dynamicTranslationId(key: string | number | null | undefined): number | null {
  if (typeof key === "number") return Number.isInteger(key) ? key : null;
  if (typeof key !== "string") return null;
  const rawId = key.startsWith("dynamic.") ? key.slice("dynamic.".length) : key;
  if (!/^\d+$/.test(rawId)) return null;
  return Number(rawId);
}

function isCatalogResponse(value: unknown): value is DynamicTranslationCatalogResponse {
  if (value == null || typeof value !== "object") return false;
  const catalog = value as Partial<DynamicTranslationCatalogResponse>;
  return (
    typeof catalog.version === "string" &&
    typeof catalog.locale === "string" &&
    catalog.values != null &&
    typeof catalog.values === "object" &&
    !Array.isArray(catalog.values) &&
    Object.entries(catalog.values).every(
      ([id, translation]) => /^\d+$/.test(id) && typeof translation === "string",
    ) &&
    Array.isArray(catalog.exactIds) &&
    catalog.exactIds.every((id) => Number.isInteger(id))
  );
}

function materializeCatalog(
  response: DynamicTranslationCatalogResponse,
  etag?: string,
): DynamicTranslationCatalog {
  const locale = normalizeDynamicTranslationLocale(response.locale);
  if (locale == null) throw new Error(`Unsupported dynamic translation locale: ${response.locale}`);
  return {
    version: response.version,
    locale,
    values: new Map(Object.entries(response.values).map(([id, value]) => [Number(id), value])),
    exactIds: new Set(response.exactIds),
    etag,
  };
}

async function requestCatalog(locale: ISOLanguageKey): Promise<DynamicTranslationCatalog> {
  const cached = catalogs.get(locale);
  const productionRequest =
    import.meta.env.MODE === "production" || import.meta.env.VITE_APP_INSTANCE_KEY === "intern";
  const headers = new Headers({
    "X-Version": !productionRequest
      ? "1.1.0"
      : (import.meta.env.__VERSION__ ?? "").replace(/(\r\n|\n|\r)/gm, ""),
    "X-Transaction-ID": transactionId(),
  });
  const accessToken = await accessTokenProvider();
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  if (cached?.etag) headers.set("If-None-Match", cached.etag);

  let response: Response;
  try {
    response = await fetch(`/api/language/catalog/${encodeURIComponent(locale)}`, {
      credentials: "include",
      headers,
    });
  } catch (error) {
    if (cached != null) return cached;
    throw error;
  }
  if (response.status === 304 && cached != null) return cached;
  if (!response.ok) {
    if (cached != null) return cached;
    throw new Error(`Dynamic translation catalog request failed with status ${response.status}`);
  }

  const body: unknown = await response.json();
  if (!isCatalogResponse(body)) {
    throw new Error("Dynamic translation catalog response is invalid");
  }
  const catalog = materializeCatalog(body, response.headers.get("ETag") ?? undefined);
  catalogs.set(locale, catalog);
  return catalog;
}

export function loadDynamicTranslationCatalog(locale: string): Promise<DynamicTranslationCatalog> {
  const normalizedLocale = normalizeDynamicTranslationLocale(locale);
  if (normalizedLocale == null) {
    return Promise.reject(new Error(`Unsupported dynamic translation locale: ${locale}`));
  }

  const existingLoad = inFlightLoads.get(normalizedLocale);
  if (existingLoad != null) return existingLoad;

  const load = requestCatalog(normalizedLocale).finally(() => {
    inFlightLoads.delete(normalizedLocale);
  });
  inFlightLoads.set(normalizedLocale, load);
  return load;
}

export function configureDynamicTranslationAccessToken(provider: AccessTokenProvider) {
  accessTokenProvider = provider;
}

export function activateDynamicTranslationCatalog(catalog: DynamicTranslationCatalog) {
  catalogs.set(catalog.locale, catalog);
  activeCatalog.value = catalog;
}

export function activateEmptyDynamicTranslationCatalog(locale: string) {
  const normalizedLocale = normalizeDynamicTranslationLocale(locale) ?? defaultLanguage;
  const catalog = emptyCatalog(normalizedLocale);
  catalogs.set(normalizedLocale, catalog);
  activeCatalog.value = catalog;
}

export async function refreshActiveDynamicTranslationCatalog() {
  const locale = activeCatalog.value.locale;
  const catalog = await loadDynamicTranslationCatalog(locale);
  if (activeCatalog.value.locale === locale) activateDynamicTranslationCatalog(catalog);
}

export function translateDynamic(key: string | number | null | undefined): string {
  const id = dynamicTranslationId(key);
  if (id == null) {
    if (key === "dynamic.undefined") {
      // eslint-disable-next-line no-console
      console.error("A component attempted to translate dynamic.undefined");
      return "";
    }
    return translationUnavailable[activeCatalog.value.locale] ?? translationUnavailable.en;
  }
  return (
    activeCatalog.value.values.get(id) ??
    translationUnavailable[activeCatalog.value.locale] ??
    translationUnavailable.en
  );
}

export function hasExactDynamicTranslation(
  key: string | number | null | undefined,
  locale?: string,
): boolean {
  const id = dynamicTranslationId(key);
  if (id == null) return false;
  const requestedLocale = normalizeDynamicTranslationLocale(locale ?? activeCatalog.value.locale);
  if (requestedLocale == null) return false;
  const catalog =
    catalogs.get(requestedLocale) ??
    (requestedLocale === activeCatalog.value.locale ? activeCatalog.value : undefined);
  return catalog?.exactIds.has(id) ?? false;
}

export function useDynamicTranslations() {
  return {
    locale: computed(() => activeCatalog.value.locale),
    t: translateDynamic,
    te: hasExactDynamicTranslation,
  };
}

export const activeDynamicTranslationLocale = computed(() => activeCatalog.value.locale);
export const activeDynamicTranslationCatalogVersion = computed(() => activeCatalog.value.version);
